import { Image } from 'expo-image'
import { cssInterop } from 'nativewind'
import React, { useMemo, useState } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'

import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import type { PhosphorIconName } from '@ui-kit/components/PhosphorIcon/iconNames'
import { COLOR, FORM_ELEMENT } from '@ui-kit/constants'
import { caption } from '@ui-kit/core/typography'

import OptionList from './OptionList'
import SelectionSlots from './SelectionSlots'
import { IS_NATIVE, searchableList } from './styles'
import type { OptionValue, SearchableListOption, SearchableListProps } from './types'
import {
  buildSections,
  getEmptyResultOptions,
  getSelectionState,
  isBlockedOption,
  pickSelection,
  removeSelectionAt,
} from './utils'

cssInterop(Image, {
  className: {
    target: 'style',
  },
})

const GROUPED_NATIVE_ICON = { size: 32, weight: 'fill', color: COLOR.grey700 } as const
const DEFAULT_ICON = { size: 24 } as const

const getOptionIconProps = (isGrouped: boolean) =>
  IS_NATIVE && isGrouped ? GROUPED_NATIVE_ICON : DEFAULT_ICON

interface OptionMediaProps {
  option: SearchableListOption
  styles: ReturnType<typeof searchableList>
  isGrouped: boolean
}

const OptionMedia = ({ option, styles, isGrouped }: OptionMediaProps) => {
  if (option.image) {
    return (
      <View className={styles.imageContainer()}>
        <Image source={{ uri: option.image }} className={styles.image()} contentFit="contain" />
      </View>
    )
  }
  if (!option.icon) {
    return null
  }

  return (
    <View className={styles.imageContainer()}>
      <PhosphorIcon name={option.icon as 'Bank'} {...getOptionIconProps(isGrouped)} />
    </View>
  )
}

const SelectedMark = ({ icon }: { icon?: PhosphorIconName }) =>
  icon ? (
    <PhosphorIcon name={icon} size={24} weight="fill" />
  ) : (
    <PhosphorIcon name="Check" size={20} weight="bold" />
  )

interface OptionItemProps {
  option: SearchableListOption
  hasImages: boolean
  isGrouped: boolean
  isLast: boolean
  isSelected: boolean
  isMultiple: boolean
  disabled: boolean
  selectedIcon?: PhosphorIconName
  testID: string
  onSelect: (value: OptionValue) => void
}

const OptionItem = ({
  option,
  hasImages,
  isGrouped,
  isLast,
  isSelected,
  ...props
}: OptionItemProps) => {
  const styles = searchableList({ hasImages, isGrouped })

  return (
    <Pressable
      className={styles.optionItem({ isLast, isSelected, isDisabled: props.disabled })}
      onPress={() => props.onSelect(option.value)}
      testID={props.testID}
      disabled={props.disabled}
      role={props.isMultiple ? 'checkbox' : 'radio'}
      aria-checked={isSelected}
    >
      <OptionMedia option={option} styles={styles} isGrouped={isGrouped} />
      <View className={styles.labelContainer()}>
        <Text className={styles.optionLabel()}>{option.label}</Text>
      </View>
      {isSelected && <SelectedMark icon={props.selectedIcon} />}
    </Pressable>
  )
}

const ListTitle = ({ title }: { title?: string }) => {
  const styles = searchableList()
  if (!title) {
    return null
  }

  return (
    <View className={styles.titleContainer()}>
      <Text className={styles.titleText()}>{title}</Text>
    </View>
  )
}

type SearchFieldProps = Pick<
  SearchableListProps,
  'searchPlaceholder' | 'searchTestID' | 'testID' | 'disabled'
> & {
  value: string
  onChangeText: (query: string) => void
}

const SearchField = (props: SearchFieldProps) => {
  const styles = searchableList()

  return (
    <View className={styles.searchContainer()}>
      {IS_NATIVE && <PhosphorIcon name="MagnifyingGlass" size={24} color={COLOR.grey500} />}
      <TextInput
        className={styles.searchInput()}
        value={props.value}
        onChangeText={props.onChangeText}
        placeholder={props.searchPlaceholder ?? 'Search...'}
        placeholderTextColor={FORM_ELEMENT.placeholderColor}
        testID={props.searchTestID ?? `${props.testID}_search`}
        editable={!props.disabled}
      />
    </View>
  )
}

const SearchableList = (props: SearchableListProps) => {
  const [searchQuery, setSearchQuery] = useState('')

  const hasImages = useMemo(() => props.options.some((option) => option.image), [props.options])

  const { filteredOptions, sections } = useMemo(
    () =>
      buildSections({
        options: props.options,
        popularOptions: props.popularOptions,
        pinnedOptions: props.pinnedOptions,
        groupBy: props.groupBy,
        locale: props.locale,
        searchQuery,
      }),
    [
      props.options,
      props.popularOptions,
      props.pinnedOptions,
      props.groupBy,
      props.locale,
      searchQuery,
    ]
  )

  const isMultiple = props.selectionMode === 'multiple'
  const selection = getSelectionState(props)

  const handleSelect = async (value: OptionValue) => {
    if (props.selectionMode === 'multiple') {
      props.onValueChange(pickSelection(value, selection))
      return
    }
    props.onValueChange(value)
    await props.onSubmit?.()
  }

  const handleRemove = (index: number) => {
    if (props.selectionMode === 'multiple') {
      props.onValueChange(removeSelectionAt(selection.selectedValues, index))
    }
  }

  const renderOption = (
    option: SearchableListOption,
    index: number,
    options: readonly SearchableListOption[]
  ) => (
    <OptionItem
      key={String(option.value)}
      option={option}
      hasImages={hasImages}
      isGrouped={props.groupBy !== undefined}
      isLast={index === options.length - 1}
      isSelected={selection.selectedValues.includes(option.value)}
      isMultiple={isMultiple}
      disabled={Boolean(props.disabled) || isBlockedOption(option.value, selection)}
      selectedIcon={props.selectedIcon}
      testID={`${props.testID}_option_${option.value}`}
      onSelect={handleSelect}
    />
  )

  return (
    <View testID={props.testID}>
      <ListTitle title={props.title} />
      {isMultiple && (
        <SelectionSlots
          options={props.options}
          selectedValues={selection.selectedValues}
          maxSelections={props.maxSelections}
          placeholderImage={props.slotPlaceholderImage}
          disabled={props.disabled}
          testID={props.testID}
          labels={props.labels}
          onRemove={handleRemove}
        />
      )}
      <SearchField
        searchPlaceholder={props.searchPlaceholder}
        searchTestID={props.searchTestID}
        testID={props.testID}
        disabled={props.disabled}
        value={searchQuery}
        onChangeText={setSearchQuery}
      />
      <OptionList
        sections={sections}
        filteredOptions={filteredOptions}
        labels={props.labels}
        isMultiple={isMultiple}
        isGrouped={props.groupBy !== undefined}
        title={props.title}
        emptyOptions={getEmptyResultOptions(props.options, filteredOptions, props.pinnedOptions)}
        renderOption={renderOption}
      />
      {props.errorMessage ? (
        <Text className={caption({ color: 'error' })}>{props.errorMessage}</Text>
      ) : null}
    </View>
  )
}

export default SearchableList
